import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Jest Basics for React
//  Chapter 9 · Testing
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `# Jest comes pre-configured with Vite (via vitest) or CRA
# For Vite add Vitest (Jest-compatible):
npm install --save-dev vitest @vitest/ui jsdom
# Or use Jest directly:
npm install --save-dev jest babel-jest @babel/preset-env @babel/preset-react`;

const CODE_1 = `// describe — group related tests
describe('Math utilities', () => {

  // test / it — individual test case
  test('adds two numbers correctly', () => {
    expect(add(2, 3)).toBe(5);
  });

  it('throws on non-number input', () => {
    expect(() => add('a', 3)).toThrow('Invalid input');
  });
});

// Common matchers
expect(value).toBe(5);                    // Strict equality (===)
expect(obj).toEqual({ a: 1, b: 2 });     // Deep equality
expect(str).toContain('hello');           // String/array contains
expect(arr).toHaveLength(3);             // Array/string length
expect(fn).toHaveBeenCalled();           // Mock was called
expect(fn).toHaveBeenCalledWith(42);     // Mock called with args
expect(value).toBeTruthy();              // Truthy check
expect(value).toBeNull();               // null check
expect(value).toBeGreaterThan(0);       // Number comparison

// Async tests
test('fetches user data', async () => {
  const user = await fetchUser(1);
  expect(user.name).toBe('Alice');
});

// Mock a module
jest.mock('../services/api', () => ({
  getUser: jest.fn().mockResolvedValue({ id: 1, name: 'Alice' }),
}));

// Setup and teardown
beforeEach(() => { /* runs before each test */ });
afterEach(()  => { /* cleanup after each test */ });
beforeAll(()  => { /* runs once before all tests */ });
afterAll(()   => { /* cleanup once after all tests */ });`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function JestBasicsForReact() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 9 · Testing</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Jest Basics for React</h1>
        <p style={styles.desc}>Write your first unit tests for React using Jest — the industry-standard test runner.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Why Test? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Why Test?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Tests give you confidence that your code works as expected and protect against regressions when you add new features. Aim for:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Unit tests</b> — test individual functions/components in isolation" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>Integration tests</b> — test how components work together" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• <b>E2E tests</b> — test complete user flows in a real browser" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── Jest Core API ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Jest Core API</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Master these Jest building blocks before writing React-specific tests." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. What is the difference between toBe and toEqual?</div>
          <div style={styles.qItem}>✦ 2. When do you use jest.fn() vs jest.spyOn()?</div>
          <div style={styles.qItem}>✦ 3. How do you test that an async function throws an error?</div>
          <div style={styles.qItem}>✦ 4. What is the --coverage flag and what does it show?</div>
          <div style={styles.qItem}>✦ 5. Write tests for a utility function: formatCurrency(1234.5, \'USD\') → \'$1,234.50\'</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
